import React, { useState, useRef } from 'react';
import { Plus, Pencil, Archive, Check, X } from 'lucide-react';

// Relative time in Swedish ("för 2 dagar sedan", "just nu").
const relativeTimeSv = (iso) => {
    const date = new Date(iso);
    if (isNaN(date.getTime())) return '';
    const rtf = new Intl.RelativeTimeFormat('sv', { numeric: 'always' });
    const minutes = Math.round((Date.now() - date.getTime()) / 60000);
    if (minutes < 1) return 'just nu';
    if (minutes < 60) return rtf.format(-minutes, 'minute');
    const hours = Math.round(minutes / 60);
    if (hours < 24) return rtf.format(-hours, 'hour');
    const days = Math.round(hours / 24);
    if (days < 7) return rtf.format(-days, 'day');
    if (days < 30) return rtf.format(-Math.round(days / 7), 'week');
    const months = Math.round(days / 30);
    if (months < 12) return rtf.format(-months, 'month');
    return rtf.format(-Math.round(months / 12), 'year');
};

/**
 * ChatGPT-style thread history sidebar for the Finn chat.
 * Presentational only — the parent owns the socket and passes the
 * `threadList` payload (newest first, non-archived) plus action callbacks.
 */
const ThreadHistoryPanel = ({ threads, onNewThread, onSelectThread, onRenameThread, onArchiveThread }) => {
    const [editingEpoch, setEditingEpoch] = useState(null);
    const [editTitle, setEditTitle] = useState('');
    const [confirmEpoch, setConfirmEpoch] = useState(null);
    // Escape unmounts the input, which can still fire its blur — this flag
    // keeps the blur-commit from re-applying a cancelled rename.
    const renameCancelledRef = useRef(false);

    const startRename = (thread) => {
        renameCancelledRef.current = false;
        setConfirmEpoch(null);
        setEditingEpoch(thread.epoch);
        setEditTitle(thread.title || '');
    };

    const cancelRename = () => {
        renameCancelledRef.current = true;
        setEditingEpoch(null);
    };

    const commitRename = (thread) => {
        if (renameCancelledRef.current) return;
        const title = editTitle.trim();
        if (title && title !== thread.title) onRenameThread(thread.epoch, title);
        setEditingEpoch(null);
    };

    return (
        <div className="finn-threads-panel">
            <style>{`
                .finn-threads-panel {
                    width: 264px;
                    flex-shrink: 0;
                    display: flex;
                    flex-direction: column;
                    background: white;
                    border-right: 1px solid #e5e7eb;
                    min-height: 0;
                }
                .finn-threads-top {
                    padding: 0.75rem;
                    border-bottom: 1px solid #f3f4f6;
                    flex-shrink: 0;
                }
                .finn-threads-new {
                    width: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.375rem;
                    background: var(--primary, #44814E);
                    color: white;
                    border: none;
                    border-radius: 10px;
                    padding: 0.55rem 0.75rem;
                    font-size: 0.8125rem;
                    font-weight: 600;
                    cursor: pointer;
                    transition: background 0.15s;
                }
                .finn-threads-new:hover {
                    background: var(--primary-hover, #32603a);
                }
                .finn-threads-list {
                    flex: 1;
                    min-height: 0;
                    overflow-y: auto;
                    padding: 0.5rem;
                }
                .finn-threads-empty {
                    text-align: center;
                    font-size: 0.8125rem;
                    color: #9ca3af;
                    padding: 1.25rem 0.75rem;
                }
                .finn-thread-item {
                    position: relative;
                    border-radius: 10px;
                    padding: 0.5rem 0.625rem;
                    cursor: pointer;
                    margin-bottom: 2px;
                    transition: background 0.15s;
                }
                .finn-thread-item:hover {
                    background: #f3f4f6;
                }
                .finn-thread-item.active {
                    background: var(--rgba-primary-1, rgba(68, 129, 78, 0.1));
                }
                .finn-thread-title {
                    font-size: 0.8125rem;
                    font-weight: 500;
                    color: #374151;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                .finn-thread-item.active .finn-thread-title {
                    color: var(--primary, #44814E);
                    font-weight: 600;
                }
                .finn-thread-time {
                    font-size: 0.6875rem;
                    color: #9ca3af;
                    margin-top: 1px;
                }
                .finn-thread-actions {
                    position: absolute;
                    top: 50%;
                    right: 0.375rem;
                    transform: translateY(-50%);
                    display: flex;
                    gap: 2px;
                    opacity: 0;
                    transition: opacity 0.15s;
                    background: inherit;
                }
                .finn-thread-item:hover .finn-thread-actions {
                    opacity: 1;
                }
                .finn-thread-action-btn {
                    background: white;
                    border: 1px solid #e5e7eb;
                    border-radius: 6px;
                    color: #6b7280;
                    cursor: pointer;
                    padding: 0.25rem;
                    display: flex;
                    align-items: center;
                    transition: all 0.15s;
                }
                .finn-thread-action-btn:hover {
                    color: #111827;
                    background: #f9fafb;
                }
                .finn-thread-rename-input {
                    width: 100%;
                    border: 1px solid var(--primary, #44814E);
                    border-radius: 6px;
                    padding: 0.25rem 0.375rem;
                    font-size: 0.8125rem;
                    font-family: inherit;
                    color: #111827;
                    outline: none;
                }
                .finn-thread-confirm {
                    display: flex;
                    align-items: center;
                    gap: 0.375rem;
                    font-size: 0.75rem;
                    color: #6b7280;
                }
                .finn-thread-confirm-btn {
                    border: 1px solid #e5e7eb;
                    border-radius: 6px;
                    background: white;
                    cursor: pointer;
                    padding: 0.2rem 0.45rem;
                    font-size: 0.75rem;
                    display: flex;
                    align-items: center;
                    gap: 0.2rem;
                    color: #6b7280;
                    transition: all 0.15s;
                }
                .finn-thread-confirm-btn.yes:hover {
                    background: #fef2f2;
                    border-color: #fecaca;
                    color: #b91c1c;
                }
                .finn-thread-confirm-btn.no:hover {
                    background: #f3f4f6;
                    color: #374151;
                }
                @media (max-width: 768px) {
                    .finn-threads-panel {
                        position: absolute;
                        left: 0;
                        top: 0;
                        bottom: 0;
                        z-index: 30;
                        box-shadow: 0 10px 28px rgba(0, 0, 0, 0.14);
                    }
                }
            `}</style>

            <div className="finn-threads-top">
                <button className="finn-threads-new" onClick={onNewThread}>
                    <Plus size={15} />
                    Ny konversation
                </button>
            </div>

            <div className="finn-threads-list">
                {(!threads || threads.length === 0) && (
                    <div className="finn-threads-empty">Inga tidigare konversationer</div>
                )}

                {(threads || []).map((thread) => {
                    const isEditing = editingEpoch === thread.epoch;
                    const isConfirming = confirmEpoch === thread.epoch;

                    return (
                        <div
                            key={thread.epoch}
                            className={`finn-thread-item ${thread.active ? 'active' : ''}`}
                            onClick={() => {
                                if (isEditing || isConfirming) return;
                                onSelectThread(thread.epoch);
                            }}
                        >
                            {isEditing ? (
                                <input
                                    className="finn-thread-rename-input"
                                    value={editTitle}
                                    autoFocus
                                    onChange={(e) => setEditTitle(e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                    onBlur={() => commitRename(thread)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') { e.preventDefault(); commitRename(thread); }
                                        if (e.key === 'Escape') { e.preventDefault(); cancelRename(); }
                                    }}
                                />
                            ) : isConfirming ? (
                                <div className="finn-thread-confirm" onClick={(e) => e.stopPropagation()}>
                                    <span>Arkivera?</span>
                                    <button
                                        className="finn-thread-confirm-btn yes"
                                        onClick={() => { setConfirmEpoch(null); onArchiveThread(thread.epoch); }}
                                    >
                                        <Check size={12} /> Ja
                                    </button>
                                    <button
                                        className="finn-thread-confirm-btn no"
                                        onClick={() => setConfirmEpoch(null)}
                                    >
                                        <X size={12} /> Avbryt
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <div className="finn-thread-title" title={thread.title || 'Namnlös konversation'}>
                                        {thread.title || 'Namnlös konversation'}
                                    </div>
                                    <div className="finn-thread-time">{relativeTimeSv(thread.lastMessageAt)}</div>
                                    <div className="finn-thread-actions">
                                        <button
                                            className="finn-thread-action-btn"
                                            title="Byt namn"
                                            aria-label="Byt namn"
                                            onClick={(e) => { e.stopPropagation(); startRename(thread); }}
                                        >
                                            <Pencil size={13} />
                                        </button>
                                        <button
                                            className="finn-thread-action-btn"
                                            title="Arkivera"
                                            aria-label="Arkivera"
                                            onClick={(e) => { e.stopPropagation(); setEditingEpoch(null); setConfirmEpoch(thread.epoch); }}
                                        >
                                            <Archive size={13} />
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default ThreadHistoryPanel;
